import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import { ApiError } from "../lib/api";
import { formatDate } from "../lib/dateFormat";
import { formatMoney } from "../lib/money";
import { t123ChargeStatusLabel, t123MembershipApi, type T123Income } from "../lib/t123MembershipApi";

/** Superadmin: ingresos de la membresía Torneos123 ($99/mes). */
export default function SuperadminMembershipIncomePage() {
  const { token } = useAuth();
  const [data, setData] = useState<T123Income | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    t123MembershipApi
      .income(token)
      .then((resp) => setData(resp.data))
      .catch((err) => setError(err instanceof ApiError ? err.message : "No se pudo cargar."));
  }, [token]);

  const by = data?.memberships_by_status ?? {};
  return (
    <div className="content-stack t123-income-page">
      <section className="card">
        <p className="eyebrow">
          <Link to="/superadmin">Superadmin</Link>
        </p>
        <h1>Membresía Torneos123</h1>
        <p className="card__subtitle">
          Cobros de {data ? formatMoney(data.price) : "$99"} al mes por jugador y club, en la cuenta de Mercado Pago de
          Torneos123.
          {data && !data.billing_enabled ? " El cobro todavía está apagado." : ""}
          {data && data.mode !== "live" ? " Modo de prueba." : ""}
        </p>
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        {data && (
          <div className="t123-income-page__totals">
            <div>
              <span>Cobrado</span>
              <strong>{formatMoney(data.totals.approved)}</strong>
            </div>
            <div>
              <span>Reembolsos y contracargos</span>
              <strong>{formatMoney(data.totals.reversed)}</strong>
            </div>
            <div>
              <span>Activas</span>
              <strong>{by.active ?? 0}</strong>
            </div>
            <div>
              <span>En gracia</span>
              <strong>{by.past_due ?? 0}</strong>
            </div>
            <div>
              <span>Sin pagar / suspendidas</span>
              <strong>{(by.no_booking ?? 0) + (by.suspended ?? 0) + (by.pending ?? 0)}</strong>
            </div>
            <div>
              <span>Por revisar a mano</span>
              <strong>{data.totals.needs_review}</strong>
            </div>
            <div>
              <span>Canceladas</span>
              <strong>{by.cancelled ?? 0}</strong>
            </div>
          </div>
        )}
      </section>
      <section className="card">
        <h2 className="card__title">Cobros recientes</h2>
        {!data ? (
          <p className="muted">Cargando…</p>
        ) : data.charges.length === 0 ? (
          <p className="muted">Todavía no hay cobros.</p>
        ) : (
          <div className="responsive-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Jugador</th>
                  <th>Club</th>
                  <th>Periodo</th>
                  <th>Monto</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {data.charges.map((charge) => (
                  <tr key={charge.id}>
                    <td>{formatDate(charge.created_at)}</td>
                    <td>{charge.player_name}</td>
                    <td>{charge.club_name}</td>
                    <td>
                      {charge.period_start ? `${formatDate(charge.period_start)} – ${formatDate(charge.period_end)}` : "—"}
                    </td>
                    <td>{formatMoney(charge.amount, charge.currency)}</td>
                    <td>
                      <span className={`status-pill t123-charge--${charge.status}`}>{t123ChargeStatusLabel(charge.status)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
